import { createMultiModelData } from '@/editor/data/multiModel/utils';
import { MULTI_SELECT_TEMPORARY_ID } from '@/editor/constant/entity';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { globalStores } from '@/editor/stores';
import { EEntityType, EContextAction, EOperateMode, EToolType, IEntityInfo } from '@/editor/types';
import { EAutoLayoutMode } from '@/editor/utils/layout/detect';
import { getMenuItemExecutionState } from './menuPolicy';
import { startLayoutGenerate } from '@/editor/utils/entityOperation';
import {
  isMultiSelectDoorWindowOnly,
  isMultiSelectFurnitureAssemblyOrSepWallOnly
} from '@/editor/utils/select/multi-select';

type TContextMenuCommand = (entityInfo: IEntityInfo) => void | Promise<void>;

export class ContextMenuCommandRegistry {
  private readonly _commands = new Map<EContextAction, TContextMenuCommand>();

  constructor() {
    this._registerDefaults();
  }

  register(type: EContextAction, command: TContextMenuCommand) {
    this._commands.set(type, command);
  }

  async dispatch(type: EContextAction, entityInfo: IEntityInfo) {
    const command = this._commands.get(type);
    if (!command) return;
    if (!this._canExecute(entityInfo, type)) return;
    await command(entityInfo);
  }

  private _registerDefaults() {
    this.register(EContextAction.REMOVE, (entityInfo) => this._removeEntity(entityInfo));
    this.register(EContextAction.COPY, (entityInfo) => this._copyEntity(entityInfo));
    this.register(EContextAction.REPLACE, (entityInfo) => this._replaceEntity(entityInfo));
    this.register(EContextAction.FLIP, (entityInfo) => this._flipEntity(entityInfo));
    this.register(EContextAction.CLEAR, (entityInfo) => this._clearRoom(entityInfo));
    this.register(EContextAction.ASSEMBLE, (entityInfo) => this._createAssembly(entityInfo));
    this.register(EContextAction.UNASSEMBLE, (entityInfo) =>
      globalStores.assemblyOperation.unAssenblyData(entityInfo.entityId)
    );
    this.register(EContextAction.SELECT_SAME_MODEL, (entityInfo) => this._selectSameModel(entityInfo));
    // 全局自动布局只对房间生效，局部自动布局只能对分区生效，约束由 `MENU_POLICY` 维持
    this.register(EContextAction.LAYOUT_FULL, (entityInfo) => startLayoutGenerate(entityInfo));
    this.register(EContextAction.LAYOUT_PARTIAL, (entityInfo) => startLayoutGenerate(entityInfo));
    this.register(EContextAction.APPLY_MATERIAL, (entityInfo) =>
      globalStores.faceOperation.applyToSameRegion(entityInfo.entityId)
    );
    this.register(EContextAction.ALIGN, () => globalStores.alignOperation.toggleAlignModeEnabled(true));
    this.register(EContextAction.ARRAY, (entityInfo) => this._startArrayPlacement(entityInfo));
  }

  private _selectSameModel(entityInfo: IEntityInfo) {
    switch (entityInfo.type) {
      case EEntityType.Furniture: {
        const furnitures = globalStores.modelStore.getSameModelFurnitures(entityInfo.entityId);
        if (furnitures.length <= 1) return;

        globalStores.editorStateStore.setSelected(
          { entityId: MULTI_SELECT_TEMPORARY_ID, type: EEntityType.MultiModel },
          {
            multiModelData: createMultiModelData(
              globalStores.multiModelOperation.transformModelInfos(
                furnitures.map(({ id }) => ({
                  entityId: id,
                  type: EEntityType.Furniture
                }))
              )
            )
          }
        );
        editorEvent.emit(EditorEventName.CONTROLLER_REFRESH);
        break;
      }
      case EEntityType.DoorWindow: {
        const doorWindows = globalStores.modelStore.getSameModelDoorWindows(entityInfo.entityId);
        if (doorWindows.length <= 1) return;

        globalStores.editorStateStore.setSelected(
          { entityId: MULTI_SELECT_TEMPORARY_ID, type: EEntityType.MultiModel },
          {
            multiModelData: createMultiModelData(
              globalStores.multiModelOperation.transformModelInfos(
                doorWindows.map(({ id }) => ({
                  entityId: id,
                  type: EEntityType.DoorWindow
                }))
              )
            )
          }
        );
        editorEvent.emit(EditorEventName.CONTROLLER_REFRESH);
        break;
      }
      default:
        break;
    }
  }

  /** 移除 entity */
  private async _removeEntity(entityInfo: IEntityInfo) {
    const { type, entityId: id } = entityInfo;
    switch (type) {
      case EEntityType.Wall:
        await globalStores.wallOperation.removeWall(id);
        break;
      case EEntityType.SeparateWall:
        globalStores.separateWallOperation.removeSeparateWall(id);
        break;
      case EEntityType.Room: {
        await globalStores.roomOperation.deleteRoomByRoomId(id);
        break;
      }
      case EEntityType.DoorWindow:
        globalStores.windowDoorOperation.deleteWindowDoor(id);
        break;
      case EEntityType.Column:
        globalStores.structOperation.removeColumn(id);
        break;
      case EEntityType.Furniture:
        globalStores.furnitureOperation.deleteFurniture(id);
        break;
      case EEntityType.Assembly:
        globalStores.assemblyOperation.removeAssembly(id);
        break;
      case EEntityType.MultiModel: {
        const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
        if (isMultiSelectFurnitureAssemblyOrSepWallOnly(multiModelData)) {
          globalStores.multiModelOperation.deleteMultiModel(multiModelData.modelInfos);
        } else if (isMultiSelectDoorWindowOnly(multiModelData)) {
          globalStores.multiModelOperation.deleteMultiDoorWindow(multiModelData.modelInfos);
        }
        break;
      }
    }
  }

  /** 清除房间/分区内的构件 */
  private async _clearRoom(entityInfo: IEntityInfo) {
    const { type, entityId: id } = entityInfo;

    switch (type) {
      case EEntityType.Region:
        await globalStores.regionOperation.clearRegion(id);
        break;
      case EEntityType.Room:
        await globalStores.roomOperation.clearRoom(id);
        break;
    }
  }

  private _copyEntity(entityInfo: IEntityInfo) {
    const { type, entityId: id } = entityInfo;
    switch (type) {
      case EEntityType.Furniture:
        globalStores.furnitureOperation.copyFurniture(id);
        break;
      case EEntityType.Assembly:
        globalStores.assemblyOperation.copyAssembly(id);
        break;
      case EEntityType.MultiModel:
        globalStores.multiModelOperation.copyMultiModel();
        break;
      case EEntityType.Column:
        globalStores.structOperation.copyColumn(id);
        break;
      case EEntityType.DoorWindow:
        globalStores.windowDoorOperation.copyDoorWindow(id);
        break;
    }
  }

  private _replaceEntity(entityInfo: IEntityInfo) {
    switch (entityInfo.type) {
      case EEntityType.Furniture:
      case EEntityType.DoorWindow:
      case EEntityType.Face:
      case EEntityType.Region:
      case EEntityType.Room:
      case EEntityType.Assembly:
      case EEntityType.MultiModel:
        editorEvent.emit(EditorEventName.REPLACE_START);
        break;
    }
  }

  private _flipEntity(entityInfo: IEntityInfo) {
    switch (entityInfo.type) {
      case EEntityType.DoorWindow:
        globalStores.windowDoorOperation.flipWindowDoor(entityInfo.entityId);
        editorEvent.emit(EditorEventName.REPLACE_END);
        break;
    }
  }

  private _createAssembly(entityInfo: IEntityInfo) {
    if (entityInfo.type !== EEntityType.MultiModel) return;

    const multiModelData = globalStores.getDataByIdType('', entityInfo.type);
    if (!multiModelData || multiModelData.modelInfos.length < 2) return;

    globalStores.assemblyOperation.createAssemblyBySelected(multiModelData.modelInfos);
  }

  private _startArrayPlacement(entityInfo: IEntityInfo) {
    switch (entityInfo.type) {
      case EEntityType.Furniture:
      case EEntityType.Assembly:
      case EEntityType.DoorWindow:
        globalStores.operateInfo = {
          mode: EOperateMode.Array,
          toolType: EToolType.empty,
          extraState: {
            arrayData: {
              entityId: entityInfo.entityId,
              entityType: entityInfo.type
            }
          }
        };
    }
  }

  private _canExecute(entityInfo: IEntityInfo, actionType: EContextAction) {
    if (!entityInfo.entityId || !entityInfo.type) return false;
    if (globalStores.editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE) return false;

    return getMenuItemExecutionState({
      sceneMode: globalStores.sceneMode,
      entityInfo,
      actionType
    }).executable;
  }
}
